import {greg2abs, isDate, daysInGregMonth, HDate, months} from '@hebcal/hdate';
import {CalOptions} from './CalOptions.js';

const {TISHREI} = months;

/**
 * Gets the R.D. days for a number, Date, or HDate
 */
function getAbs(d: Date | HDate | number): number {
  if (typeof d === 'number') return d;
  if (isDate(d)) return greg2abs(d);
  if (HDate.isHDate(d)) return d.abs();
  throw new TypeError(`Invalid date type: ${d}`);
}

function getYear(options: CalOptions): number {
  if (options.year !== undefined) {
    return Number(options.year);
  }
  return options.isHebrewYear
    ? new HDate().getFullYear()
    : new Date().getFullYear();
}

const MAX_NUM_YEARS = 2000;

/**
 * Parse options object to determine start & end days
 * @internal
 */
export function getStartAndEnd(options: CalOptions): number[] {
  const {start: start0, end: end0} = options;
  if ((start0 === undefined) !== (end0 === undefined)) {
    throw new TypeError('options.start requires options.end');
  }
  if (start0 !== undefined && end0 !== undefined) {
    const start = getAbs(start0);
    const end = getAbs(end0);
    if (end - start > 365 * MAX_NUM_YEARS) {
      throw new RangeError(`Date range exceeds ${MAX_NUM_YEARS} years`);
    }
    return [start, end];
  }
  const isHebrewYear = Boolean(options.isHebrewYear);
  const theYear = getYear(options);
  if (isNaN(theYear)) {
    throw new RangeError(`Invalid year ${options.year}`);
  }
  if (isHebrewYear && theYear < 1) {
    throw new RangeError(`Invalid Hebrew year ${theYear}`);
  }
  const theMonth = getMonth(options);
  const numYears = Number(options.numYears) || 1;
  if (numYears > MAX_NUM_YEARS) {
    throw new RangeError(`options.numYears exceeds ${MAX_NUM_YEARS}`);
  }
  if (isHebrewYear) {
    return startEndHebrew(theMonth, theYear, numYears);
  } else {
    return startEndGregorian(theMonth, theYear, numYears);
  }
}

function getMonth(options: CalOptions): number {
  if (options.month) {
    if (options.isHebrewYear) {
      return HDate.monthNum(options.month);
    }
    if (typeof options.month === 'number') {
      return options.month;
    }
  }
  return NaN;
}

function startEndGregorian(
  theMonth: number,
  theYear: number,
  numYears: number
): number[] {
  const gregMonth = theMonth ? theMonth - 1 : 0;
  const startGreg = new Date(theYear, gregMonth, 1);
  if (theYear < 100) {
    startGreg.setFullYear(theYear);
  }
  const startAbs = greg2abs(startGreg);
  let endAbs: number;
  if (theMonth) {
    endAbs = startAbs + daysInGregMonth(theMonth, theYear) - 1;
  } else {
    const endYear = theYear + numYears;
    const endGreg = new Date(endYear, 0, 1);
    if (endYear < 100) {
      endGreg.setFullYear(endYear);
    }
    endAbs = greg2abs(endGreg) - 1;
  }
  return [startAbs, endAbs];
}

function startEndHebrew(
  theMonth: number,
  theYear: number,
  numYears: number
): number[] {
  const startDate = new HDate(1, theMonth || TISHREI, theYear);
  let startAbs = startDate.abs();
  const endAbs = theMonth
    ? startAbs + startDate.daysInMonth()
    : new HDate(1, TISHREI, theYear + numYears).abs() - 1;
  // for full Hebrew year, start on Erev Rosh Hashana which
  // is technically in the previous Hebrew year
  // (but conveniently lets us get candle-lighting time for Erev)
  if (!theMonth && theYear > 1) {
    startAbs--;
  }
  return [startAbs, endAbs];
}
